export const SVG_LOGO_CONTENT_TYPE = "image/svg+xml;charset=utf-8";

const LOGO_UNICODE_LOG_PREFIX = "[logo-unicode]";

export function logLogoUnicodeDiagnostics(
  stage: string,
  value: string | null | undefined
): void {
  if (process.env.NODE_ENV === "test" || !value) {
    return;
  }

  const svg = value;
  const hasReplacement = svg.includes("\uFFFD");
  const replacementIndexes: number[] = [];

  for (let index = 0; index < svg.length; index += 1) {
    if (svg[index] === "\uFFFD") {
      replacementIndexes.push(index);
      console.warn(
        LOGO_UNICODE_LOG_PREFIX,
        stage,
        "REPLACEMENT CHARACTER",
        index,
        svg.slice(Math.max(0, index - 30), index + 30)
      );
    }
  }

  console.info(LOGO_UNICODE_LOG_PREFIX, stage, {
    length: svg.length,
    hasReplacement,
    replacementCount: replacementIndexes.length,
    sample: svg.slice(0, 120),
  });
}

export function logLogoUploadBytesDiagnostics(
  stage: string,
  bytes: Uint8Array
): void {
  if (process.env.NODE_ENV === "test") {
    return;
  }

  let utf8Valid = true;
  let decoded = "";
  try {
    decoded = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    utf8Valid = false;
    decoded = new TextDecoder().decode(bytes);
  }

  logLogoUnicodeDiagnostics(`${stage}:decoded`, decoded);

  console.info(LOGO_UNICODE_LOG_PREFIX, `${stage}:bytes`, {
    byteLength: bytes.length,
    utf8Valid,
    head: Array.from(bytes.slice(0, 16)),
    contentType: SVG_LOGO_CONTENT_TYPE,
  });
}

export type LogoUploadPayload =
  | { kind: "url"; url: string }
  | { kind: "bytes"; bytes: Uint8Array; contentType: string; extension: string };

/** Normalize logo upload input into UTF-8 bytes or a passthrough URL. */
export function resolveLogoUploadPayload(content: string): LogoUploadPayload {
  const trimmed = content.trim();

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return { kind: "url", url: trimmed };
  }

  if (trimmed.startsWith("data:")) {
    const comma = trimmed.indexOf(",");
    const header = trimmed.slice(0, comma);
    const payload = trimmed.slice(comma + 1);
    const mimeMatch = header.match(/data:([^;,]+)/);
    const mime = mimeMatch?.[1] || "image/png";
    const extension = mime.includes("svg")
      ? "svg"
      : mime.includes("jpeg")
        ? "jpg"
        : "png";
    const isBase64 = /;base64/i.test(header);
    const bytes = isBase64
      ? Uint8Array.from(atob(payload), (char) => char.charCodeAt(0))
      : new TextEncoder().encode(decodeURIComponent(payload));
    const contentType = mime.includes("svg") ? SVG_LOGO_CONTENT_TYPE : mime;

    return { kind: "bytes", bytes, contentType, extension };
  }

  const svgIndex = trimmed.indexOf("<svg");
  const markup = (svgIndex >= 0 ? trimmed.slice(svgIndex) : trimmed).replace(/^\uFEFF/, "");
  return {
    kind: "bytes",
    bytes: new TextEncoder().encode(markup),
    contentType: SVG_LOGO_CONTENT_TYPE,
    extension: "svg",
  };
}

/** Convert any logo value into a single `<img src>` string. Used once at each boundary. */
export function toImgSrc(raw: string | null | undefined): string | null {
  if (!raw?.trim()) {
    return null;
  }

  const trimmed = raw.trim();

  if (
    trimmed.startsWith("http://")
    || trimmed.startsWith("https://")
    || trimmed.startsWith("data:")
  ) {
    return trimmed;
  }

  const svgIndex = trimmed.indexOf("<svg");
  if (svgIndex !== -1) {
    const markup = trimmed.slice(svgIndex);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  }

  return null;
}

/** Read logo string from heterogeneous API responses. No normalization. */
export function extractLogoFromApiResponse(
  response: Record<string, unknown> | null | undefined
): string | null {
  if (!response) {
    return null;
  }

  const nested = response.data as Record<string, unknown> | undefined;
  const candidates = [
    response.logo_svg,
    response.logo_url,
    response.logo,
    response.image_url,
    nested?.logo_svg,
    nested?.logo_url,
    nested?.logo,
    nested?.image_url,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function expandShortHex(hex: string): string {
  if (hex.length !== 4) {
    return hex;
  }
  return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
}

/** Resolve CSS variables and return a valid hex color for API/generation calls. */
export function normalizeHexColor(
  color: string | undefined,
  fallback: string
): string {
  const resolved = resolveCssColor(color).trim();
  if (!HEX_COLOR_RE.test(resolved)) {
    return fallback;
  }
  return expandShortHex(resolved.toLowerCase());
}

export function buildPersistableBrandColors(companyImage: {
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  font?: string;
}) {
  return {
    primary_color: normalizeHexColor(companyImage.primaryColor, "#5B8DEF"),
    secondary_color: normalizeHexColor(companyImage.secondaryColor, "#22D3EE"),
    tertiary_color: normalizeHexColor(companyImage.tertiaryColor, "#0F172A"),
    font: companyImage.font || "Roboto",
  };
}

/** Background the logo will render on (matches ManageDashboard preview). */
export function resolveDisplayBackground(
  companyImage: { primaryColor?: string },
  business: { details?: { primary_color?: string } }
): string {
  return normalizeHexColor(
    companyImage.primaryColor || business.details?.primary_color,
    "#FFFFFF"
  );
}

export function resolveCssColor(color: string | undefined): string {
  if (!color) return "#5B8DEF";
  if (!color.startsWith("var(")) return color;

  const varName = color.match(/var\((--[^)]+)\)/)?.[1];
  if (!varName || typeof document === "undefined") return color;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return value || color;
}
